import type {
  ApiIsCalculatedMode,
  EstimationItemCostResponse,
  EstimationItemResourceResponse,
  EstimationUnit,
} from '@/api/schema/data-contracts';

export enum eEstimationDetailTab {
  project = 'project',
  metadata = 'metadata',
  estimation = 'estimation',
  assumptions = 'assumptions',
  resource = 'resource',
  resourceCost = 'resourceCost',
  directCost = 'directCost',
  costSummary = 'costSummary',
  managementReport = 'managmentReport',
  review = 'review',
}

export type EstimationItemTableRecordParameter = {
  id: string;
  order: number;
  parameterName: string;
  value: number | undefined;
  estimationModelItemParameterId: string | undefined;
  methodologyParameterId: string | undefined;
};

export type EstimationItemsTableRecordCommon = {
  id: string;
  reference: string;
  order: number;
  name: string;
  parentId: string | undefined;
  description: string | undefined;
  isOmitted: boolean;
  omitReason: string | undefined;
  estimationModelGroupId: string;
  isParentBasedCalculation: boolean;
  assumptionsCount: number;
  commentsCount: number;
  isRequired: boolean;
  estimationModelItemId: string | undefined;
};

export type CostEstimationItemsTableRecord = EstimationItemsTableRecordCommon & {
  estimationItemCosts: EstimationItemCostResponse[];
  areFilledSubItemsEstimationItemCosts: boolean;
};

export type ResourceEstimationItemsTableRecord = EstimationItemsTableRecordCommon & {
  phaseLeadId: string | undefined;
  methodology: { id: string; name: string; order: number } | undefined;
  isCalculated: ApiIsCalculatedMode;
  hasEstimationModelItemParameters: boolean;
  calculatedResult: number | undefined;
  childrenCount: number;
  params: EstimationItemTableRecordParameter[];
  estimationModelItemParameters:
    | {
        id: string;
        defaultValue: number | undefined;
      }[]
    | undefined;
  resources: EstimationItemResourceResponse[] | undefined;
  areFilledSubItemsResources: boolean;
  /** Descendants' parameter values summed per parameter name; empty when the item has no sub-items. */
  subItemsParamValueSums: Map<string, number>;
};

export type MethodologyData = {
  id: string;
  name: string;
  order: number;
};

export type EstimationItemDetail = {
  id: string;
  reference: string;
  name: string;
  description: string;
  phaseLeadId: string | undefined;
  methodologyId: string | undefined;
  groupName: string;
  isParentBasedCalculation: boolean;
  calculatedResult?: number;
  hasEstimationModelItemParameters: boolean;
  unit: EstimationUnit;
  subItemsCalculatedResultsSum?: number;
  assumptionsCount: number;
  params: EstimationItemTableRecordParameter[];
  parentId: string | undefined;
  resources?: EstimationItemResourceResponse[] | undefined;
};

export type CostEstimationItemDetail = {
  id: string;
  reference: string;
  name: string;
  description: string;
  groupName: string;
  isParentBasedCalculation: boolean;
  hasEstimationModelItemParameters: boolean;
  subItemsCalculatedResultsSum?: number;
  assumptionsCount: number;
  parentId: string | undefined;
  estimationItemsCosts?: EstimationItemCostResponse[] | undefined;
};

export type ProjectBasicsModel = {
  projectName: string;
  projectTypeId: string;
  methodologyId: string;
  defaultEstimationUnit: string;
  estimationLanguage: string;
  planningResponsibleId: string;
  projectReviewerId: string | null;
  additionalContributorIds: string[];
};

export type ProjectInfoReviewModel = {
  projectName: string;
  planningResponsibleId: string;
  projectReviewerId?: string;
  additionalContributorIds?: string[];
};

export type EstimationItemEditModel = {
  id: string;
  name: string;
  description: string;
  phaseLeadId: string | undefined;
  methodologyId: string | undefined;
  isParentBasedCalculation: boolean;
};

export type CostEstimationItemEditModel = {
  id: string;
  name: string;
  description: string;
  isParentBasedCalculation: boolean;
};

export type EstimationItemsFormModelRow = {
  id: string;
  name: string;
  description: string | null;
  isIncluded: boolean;
  omitReason: string | null;
  shouldDelete: boolean;
  methodologyId: string | null;
  parentId: string | null;
  groupId: string;
  phaseLeadId: string | null;
  parameters: {
    parameterName: string;
    value: number | null;
  }[];
  resources?: {
    resourceTypeId: string;
    value: number | null;
  }[];
};

export type EstimationItemsEditFormModel = {
  mainEstimationItem?: EstimationItemEditModel;
  estimationItems: EstimationItemsFormModelRow[];
};

export type CostEstimationItemsFormModelRow = {
  id: string;
  name: string;
  description: string | null;
  isIncluded: boolean;
  omitReason: string | null;
  shouldDelete: boolean;
  parentId: string | null;
  groupId: string;
  estimationItemCosts?: {
    costTypeId: string;
    price: number | null;
  }[];
};

export type CostEstimationItemsEditFormModel = {
  mainEstimationItem?: CostEstimationItemEditModel;
  estimationItems: CostEstimationItemsFormModelRow[];
};

export type EstimationItemActionCallback = (estimationItemId: string) => void;

export type EstimationItemNameActionCallback = (estimationItemId: string, name: string) => void;

export type EstimationItemMethodologyChangeCallback = (
  estimationItemId: string,
  methodologyId: string | null,
) => Promise<boolean>;

export type EstimationItemIsIncludedActionPromiseCallback = (
  estimationItemId: string,
  isIncluded: boolean,
) => Promise<void>;

export type EstimationItemIsIncludedActionCallback = (
  estimationItemId: string,
  isIncluded: boolean,
) => void;

export type EstimationGroupActionCallback = (estimationGroupId?: string) => void;

export interface CostModel {
  currencyId: string;
  resourceTypeCosts: {
    resourceTypeId: string;
    costTypeId: string;
    price: number;
  }[];
}

export interface CommentModel {
  estimationItemId: string;
  // TODO:
}

export type RecalculateCallback = (
  formModelEstimationItems: EstimationItemsFormModelRow[],
) => Map<string, number>;
